import { CalculationsAPI } from '../../shared/services/calculations/api';
import {
  createInitialNavigationState,
  handleEquipmentSelectComplete,
} from '../../shared/utils/equipmentNavigation';

const DEFAULT_CONSOLE = {
  id: 'console-basic-001',
  name: 'Блок управления бассейном',
  type: 'console',
  price: 45000,
  calculatedPrice: 45000,
  description: 'Простая консоль управления насосом',
  features: ['Управление насосом', 'Индикация работы'],
  model: 'console.glb',
  image: '/models/consoles/console.webp',
  isDefault: true,
};

function upperName(p) {
  return (p?.name || '').toUpperCase();
}

/**
 * Насос для финального шага онбординга: Glong 370S (или ближайший Glong), иначе первый насос каталога.
 */
export function pickOnboardingShowcasePump(pumps) {
  if (!pumps?.length) return null;
  const withGlong = pumps.filter((p) => upperName(p).includes('GLONG'));
  const pool = withGlong.length ? withGlong : pumps;

  const with370 = pool.filter((p) => {
    const u = upperName(p);
    return (
      u.includes('370S') ||
      u.includes('370 S') ||
      u.includes('370-S') ||
      u.includes('370S2') ||
      u.includes('370 S2')
    );
  });
  if (with370.length) return with370[0];
  if (withGlong.length) return withGlong[0];
  return pumps[0];
}

/**
 * Собирает демо-насос + консоль (+ песок моноблока) только для подмешивания в список сцены.
 * @param {object} pump
 * @param {object[]|null|undefined} selectedSandRows
 * @returns {{ demoPump: object; items: object[] }|null}
 */
export function buildOnboardingShowcaseSceneOverlay(pump, selectedSandRows) {
  if (!pump) return null;
  const items = [{ ...pump, type: 'pump' }, { ...DEFAULT_CONSOLE }];
  const sands = Array.isArray(selectedSandRows) ? selectedSandRows : [];
  sands.forEach((sandItem) => {
    items.push({ ...sandItem, type: 'sand' });
  });
  return { demoPump: pump, items };
}

/**
 * Список для Scene3D: базовый `selectedEquipment` из стора + опциональный overlay онбординга.
 * Правая панель читает только базовый стор без overlay.
 * @param {unknown[]} selectedEquipment
 * @param {null | { demoPump: object; items: object[] }} overlay
 * @returns {unknown[]}
 */
export function mergeOnboarding3dOverlayIntoSelectedEquipment(selectedEquipment, overlay) {
  const base = Array.isArray(selectedEquipment) ? selectedEquipment : [];
  if (!overlay || !overlay.demoPump || !Array.isArray(overlay.items) || overlay.items.length === 0) {
    return base;
  }
  const pump = overlay.demoPump;
  const filtered = base.filter(
    (item) =>
      !['pump', 'console', 'heating_not_needed', 'no-heating', 'lighting_not_needed', 'ladder_not_needed', 'disinfection_not_needed'].includes(
        item.type
      ) && !(pump.isMonoblock && item.isSand)
  );
  return [...filtered, ...overlay.items];
}

/**
 * @deprecated Используйте mergeOnboarding3dOverlayIntoSelectedEquipment(base, overlay).
 */
export function mergeSelectedEquipmentFor3dOnboarding(selectedEquipment, overlay = null) {
  return mergeOnboarding3dOverlayIntoSelectedEquipment(selectedEquipment, overlay);
}

/** Снимок полей, которые меняет демо-насос, чтобы вернуть после шага онбординга. */
export function clonePumpTourSnapshot(prev) {
  return {
    selectedPump: prev.selectedPump ? { ...prev.selectedPump } : null,
    selectedConsole: prev.selectedConsole ? { ...prev.selectedConsole } : null,
    selectedSand: Array.isArray(prev.selectedSand) ? prev.selectedSand.map((s) => ({ ...s })) : [],
    selectedEquipment: (prev.selectedEquipment || []).map((i) => ({ ...i })),
    navigationState: {
      originalStep: prev.navigationState?.originalStep ?? null,
      cancelledStepsStack: [...(prev.navigationState?.cancelledStepsStack || [])],
      stepContext: { ...(prev.navigationState?.stepContext || {}) },
    },
  };
}

export function restorePumpTourSnapshot(setEquipment, snapshot) {
  if (!snapshot) return;
  setEquipment((prev) => ({
    ...prev,
    selectedPump: snapshot.selectedPump ? { ...snapshot.selectedPump } : null,
    selectedConsole: snapshot.selectedConsole ? { ...snapshot.selectedConsole } : null,
    selectedSand: Array.isArray(snapshot.selectedSand)
      ? snapshot.selectedSand.map((s) => ({ ...s }))
      : [],
    selectedEquipment: (snapshot.selectedEquipment || []).map((i) => ({ ...i })),
    navigationState: {
      originalStep: snapshot.navigationState?.originalStep ?? null,
      cancelledStepsStack: [...(snapshot.navigationState?.cancelledStepsStack || [])],
      stepContext: { ...(snapshot.navigationState?.stepContext || {}) },
    },
  }));
}

/**
 * Как handlePumpSelect в usePumpSelection — чтобы 3D и смета совпадали с обычным выбором в UI.
 * @param {React.MutableRefObject<Object|null>|null} snapshotRef — куда положить состояние до подмены насоса (для отката).
 */
export async function ensureOnboardingShowcasePump(pump, allEquipment, setEquipment, snapshotRef = null) {
  if (!pump) return;

  setEquipment((prev) => {
    if (snapshotRef && !snapshotRef.current) {
      snapshotRef.current = clonePumpTourSnapshot(prev);
    }
    return prev;
  });

  let selectedSand = [];
  if (pump.isMonoblock) {
    try {
      selectedSand = await CalculationsAPI.getMonoblockSandInfo(pump, allEquipment);
    } catch {
      selectedSand = [];
    }
  }

  setEquipment((prev) => {
    const already =
      prev.selectedPump?.id === pump.id &&
      (prev.selectedEquipment || []).some((item) => item.type === 'pump' && item.id === pump.id);
    if (already) return prev;

    const filtered = (prev.selectedEquipment || []).filter(
      (item) =>
        !['pump', 'console', 'heating_not_needed', 'no-heating', 'lighting_not_needed', 'ladder_not_needed', 'disinfection_not_needed'].includes(
          item.type
        ) && !(pump.isMonoblock && item.isSand)
    );

    const updatedNav = handleEquipmentSelectComplete(prev.navigationState, 'pump');
    const newEquipmentList = [...filtered, { ...pump, type: 'pump' }, DEFAULT_CONSOLE];

    if (selectedSand?.length) {
      selectedSand.forEach((sandItem) => {
        newEquipmentList.push({ ...sandItem, type: 'sand' });
      });
    }

    return {
      ...prev,
      selectedPump: pump,
      selectedConsole: DEFAULT_CONSOLE,
      selectedSand: pump.isMonoblock ? selectedSand : prev.selectedSand,
      heatingNotNeeded: null,
      lightingNotNeeded: null,
      ladderNotNeeded: null,
      disinfectionNotNeeded: null,
      selectedEquipment: newEquipmentList,
      navigationState: updatedNav || createInitialNavigationState(),
    };
  });
}
